import { useState } from 'react'
import { useCookies } from 'react-cookie'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil'

import { JoinTermsValue, MvnoCheckValue } from '@/features/join/store'
import {
  RefundAccount,
  RefundName,
  RefundTermYN,
  RefundTypeState,
  useRefundResetCallback,
} from '@/features/support/refund/store'
import {
  TYPE_CANCEL,
  TYPE_CANCEL_SUBMITCAPTCHA,
  TYPE_CHECK_MVNOMEMBER,
  TYPE_CHECK_VULNERABLE,
  TYPE_JOIN,
  TYPE_JOIN_SUBMITCAPTCHA,
  TYPE_LOGIN,
  TYPE_LOGINCERT,
  TYPE_REFUND,
  TYPE_REFUND_SUBMITCAPTCHA,
} from '@/shared/constant/cmdtype'
import {
  useCancelQueryData,
  useCancelSecurityConfirmQueryData,
  useCheckMvnoMemberDataMutation,
  useCheckVulnerableQueryData,
  useJoinQueryData,
  useJoinSecurityConfirmQueryData,
  useLoginCertSubmitQueryData,
  useLoginQueryData,
  useRefundQueryData,
  useRefundSecurityConfirmQueryData,
} from '@/shared/hooks/reactquery'
import { useDoubleModal, useModal } from '@/shared/hooks/useModal'
import {
  certCheck,
  CertNumState,
  DouModalOption,
  JoinTermsSelector,
  RecapAuthToken,
  RefreshRecapAuthToken,
  SecurityNumState,
  SecurityTranIdState,
  TelNumState,
  TelTypeState,
  TimeOutState,
  TranIdState,
} from '@/shared/store'
import { RefundRequestType, RefundResponseType, SubscriptState } from '@/shared/types/type'

const useSubscriptQuery = () => {
  const navigate = useNavigate()
  const today = dayjs(new Date())
  const { isOpen, toggle } = useModal()
  const COOKIE_KEY = 'Famliycare'
  const [cookies, setCookie] = useCookies([COOKIE_KEY])
  const CookieHours = today.add(+1, 'hour')
  const { douIsOpen, douToggle } = useDoubleModal()
  const TelNum = useRecoilValue(TelNumState)
  const SecurityNum = useRecoilValue(SecurityNumState)
  const SecurityTranId = useRecoilValue(SecurityTranIdState)
  const TelType = useRecoilValue(TelTypeState)
  const CertNum = useRecoilValue(CertNumState)
  const TermsData = useRecoilValue(JoinTermsSelector)
  const Account = useRecoilValue(RefundAccount)
  const Name = useRecoilValue(RefundName)
  const RefundTerm = useRecoilValue(RefundTermYN)
  const RefundType = useRecoilValue(RefundTypeState)
  const setCertCheck = useSetRecoilState(certCheck)
  const ResetDouModalOption = useResetRecoilState(DouModalOption)
  const ResetSecurityNum = useResetRecoilState(SecurityNumState)
  const ResetCertNum = useResetRecoilState(CertNumState)
  const setTimeOut = useSetRecoilState(TimeOutState)
  const setDouModalSetting = useSetRecoilState(DouModalOption)
  const [TranId, setTranId] = useRecoilState(TranIdState)
  const [termsAgree, setTermsAgree] = useRecoilState(JoinTermsValue)
  const ResetTimeOut = useResetRecoilState(TimeOutState)
  const AuthToken = useRecoilValue(RecapAuthToken)
  const setRefreshReCaptcha = useSetRecoilState(RefreshRecapAuthToken)
  const [mvnoCheckValue, setMvnoCheckValue] = useRecoilState(MvnoCheckValue)

  const resetCallback = useRefundResetCallback()

  const [modalSetting, setModalSetting] = useState<SubscriptState>({
    type: '',
    option: '',
    setOption: 0,
  })
  const { mutateAsync: CheckMvmoSubmit } = useCheckMvnoMemberDataMutation(
    TYPE_CHECK_MVNOMEMBER,
    TelType,
    TelNum[0].state,
  )
  const { mutateAsync: LoginCertSubmit } = useLoginCertSubmitQueryData(
    TYPE_LOGINCERT,
    TelType,
    TelNum[0].state,
    AuthToken,
  )
  const { mutateAsync: CancelSecuritySubmit } = useCancelSecurityConfirmQueryData(
    TYPE_CANCEL_SUBMITCAPTCHA,
    SecurityTranId,
    TelType,
    TelNum[0].state,
    SecurityNum,
  )
  const { mutateAsync: JoinSecuritySubmit } = useJoinSecurityConfirmQueryData(
    TYPE_JOIN_SUBMITCAPTCHA,
    SecurityTranId,
    TelType,
    TelNum[0].state,
    SecurityNum,
  )
  const { mutateAsync: RefundSecuritySubmit } = useRefundSecurityConfirmQueryData(
    TYPE_REFUND_SUBMITCAPTCHA,
    SecurityTranId,
    TelType,
    TelNum[0].state,
    SecurityNum,
  )
  const { mutateAsync: cancelSubmit, isLoading: CancelIsLoading } = useCancelQueryData(
    TYPE_CANCEL,
    TelType,
    TelNum[0].state,
    TranId,
    Number(CertNum),
  )
  const { mutateAsync: joinSubmit, isLoading: JoinIsLoading } = useJoinQueryData(
    TYPE_JOIN,
    TelType,
    TelNum[0].state,
    TranId,
    Number(CertNum),
    TermsData.TermsIdx,
    termsAgree,
  )
  const refundCompleteTitle = TelType === 0 ? '환불 접수 완료' : '서비스 해지 및 환불 접수 완료'

  const { mutateAsync: refundSubmit, isLoading: RefundIsLoading } = useRefundQueryData(
    TYPE_REFUND,
    TelType,
    TelNum[0].state,
    TranId,
    Number(CertNum),
    {
      BankName: RefundType,
      Accountnumber: Account[0].state,
      AccountName: Name[0].state,
      TermsAgree: RefundTerm,
    } as RefundRequestType,
  )
  const { mutateAsync: loginSubmit, isLoading: LoginIsLoading } = useLoginQueryData(
    TYPE_LOGIN,
    TelNum[0].state,
    TranId,
    Number(CertNum),
  )
  const { mutateAsync: checkVulnerableSubmit } = useCheckVulnerableQueryData(
    TYPE_CHECK_VULNERABLE,
    TelType,
    TelNum[0].state,
  )

  const cancel = async () => {
    setModalSetting({ type: '', option: '', setOption: 0 })
    switch (true) {
      case !TranId:
        await setModalSetting({
          type: 'alert',
          option: '',
          setOption: 201,
        })
        break
      default:
        cancelSubmit()
          .then(async (data) => {
            if (data?.Header?.ErrCode === 0) {
              await setModalSetting({
                type: 'alert',
                option: '',
                setOption: 203,
              })
            } else {
              await setModalSetting({
                type: 'alert',
                option: data?.Header?.ErrMsg,
                setOption: 0,
              })
              ResetCertNum()
            }
          })
          .finally(() => setCertCheck(false))
    }
    toggle()
  }
  const executeJoin = async () => {
    await joinSubmit().then(async (data) => {
      if (data?.Header?.ErrCode === 0) {
        await setModalSetting({
          type: 'alert',
          option: '',
          setOption: 218, //2026-09-18 가입완료 문구 통일
        })
        setCertCheck(false)
      } else {
        await setModalSetting({
          type: 'alert',
          option: data?.Header?.ErrMsg,
          setOption: 0,
        })
        if (TelType === 1 && mvnoCheckValue) await setMvnoTermsAgree(0)
        ResetCertNum()
      }
    })
  }

  const join = async () => {
    document.getElementById('common_btn')?.blur()
    setModalSetting({ type: '', option: '', setOption: 0 })
    switch (true) {
      case !TranId:
        await setModalSetting({
          type: 'alert',
          option: '',
          setOption: 201,
        })
        break
      case !TermsData?.Terms.filter(
        (terms) => terms.Required === 1 && terms.Order !== undefined,
      ).every((terms) => termsAgree[terms.Order! - 1] === 1): // 필수약관 동의 여부
        await setModalSetting({
          type: 'alert',
          option: '',
          setOption: 217,
        })
        break
      default:
        if (TelType === 1 && mvnoCheckValue) {
          await setMvnoTermsAgree(1)
        }
        await checkVulnerableSubmit().then(async (data) => {
          if (data?.Header?.ErrCode === 0) {
            const { Vulnerable, Message, AgePopUpYN } = data?.Body ?? {}
            if (Vulnerable === 'Y' || AgePopUpYN === 'Y') {
              setModalSetting({
                type: 'Vulnerable',
                option: '',
                setOption: 0,
              })
              toggle()
              return
            } else if (Vulnerable === 'X') {
              setModalSetting({
                type: 'alert',
                option: Message,
                setOption: 0,
              })
              toggle()
              return
            } else {
              executeJoin()
            }
          }
        })
    }
    toggle()
  }

  const refund = async () => {
    setModalSetting({ type: '', option: '', setOption: 0 })
    switch (true) {
      case !TranId:
        await setModalSetting({
          type: 'alert',
          option: '',
          setOption: 201,
        })
        break
      default:
        refundSubmit()
          .then(async (data) => {
            if (data?.Header?.ErrCode === 0) {
              resetCallback()
              if (TelType !== 0 && data?.Body?.UseDay === 0) {
                await setModalSetting({
                  type: 'alert',
                  option:
                    '서비스 해지가 완료되었습니다.\n가입 당일 해지 고객은 요금이 부과되지 않습니다.',
                  setOption: 0,
                })
              } else {
                openRefundModal({
                  title: refundCompleteTitle,
                  content: refundResultContent(data?.Body),
                })
              }
            } else {
              if (data?.Header?.ErrCode === 1033) {
                //인증번호 3회 오류
                resetCallback()
              }
              if (data?.Header?.ErrCode === 1066 || data?.Header?.ErrCode === 1067) {
                resetCallback()
                openAlertModal({
                  message: data?.Header?.ErrMsg,
                  setOption: data?.Header?.ErrCode,
                })
              } else {
                await setModalSetting({
                  type: 'alert',
                  option: data?.Header?.ErrMsg,
                  setOption: 0,
                })
              }
              ResetCertNum()
            }
          })
          .finally(() => setCertCheck(false))
    }
    toggle()
  }
  const login = async () => {
    setModalSetting({ type: '', option: '', setOption: 0 })
    switch (true) {
      case !TranId:
        await setModalSetting({
          type: 'alert',
          option: '',
          setOption: 201,
        })
        break
      default:
        loginSubmit()
          .then(async (data) => {
            if (data?.Header?.ErrCode === 0) {
              document.body.style.overflow = 'unset'
              await setCookie(COOKIE_KEY, TelNum[0].state, {
                path: '',
                expires: CookieHours.toDate(),
              })
              navigate('/userPage/main')
            } else {
              await setModalSetting({
                type: 'alert',
                option: data?.Header?.ErrMsg,
                setOption: 0,
              })
            }
            ResetCertNum()
          })
          .finally(() => setCertCheck(false))
    }
    toggle()
  }
  const loginCertSubmit = async () => {
    setModalSetting({ type: '', option: '', setOption: 0 })
    switch (true) {
      case TelNum[0].state.length < 11:
        await setModalSetting({
          type: 'alert',
          option: '',
          setOption: 200,
        })
        break
      default:
        await LoginCertSubmit().then(async (data) => {
          if (data?.Header?.ErrCode === 0) {
            await ResetTimeOut()
            setTranId(data?.Body?.TranID)
            setTimeOut(data?.Body?.TimeOut)
            setCertCheck(true)
            await setModalSetting({
              type: 'alert',
              option: '',
              setOption: 215,
            })
          } else {
            await setModalSetting({
              type: 'alert',
              option: data?.Header?.ErrMsg,
              setOption: 0,
            })
          }
        })
    }
    setRefreshReCaptcha((r: boolean) => !r)
    toggle()
  }
  const secureModalCall = async () => {
    setModalSetting({ type: '', option: '', setOption: 0 })
    setMvnoCheckValue(false)
    setCertCheck(false)

    if (validationCheck()) {
      // kt 인지 아닌지
      if (TelType === 1 && window.location.pathname.includes('join')) {
        checkMvno()
      } else {
        securityCheck()
      }
    }
  }

  const securityCheck = () => {
    document.getElementById('phone_input')?.blur()
    document.getElementById('cert_button')?.blur()
    setModalSetting({
      type: 'security',
      option: '',
      setOption: 0,
    })
    !isOpen && toggle()
  }
  const openRefundModal = (option: { title: string; content: string }) => {
    document.getElementById('phone_input')?.blur()
    document.getElementById('cert_button')?.blur()
    setModalSetting({
      type: 'refund',
      option: option,
      setOption: 0,
    })
    !isOpen && toggle()
  }
  const openAlertModal = (alertOption: { message: string; setOption: number }) => {
    setModalSetting({
      type: 'alert',
      option: alertOption.message,
      setOption: alertOption.setOption,
    })
    !isOpen && toggle()
  }
  const openConfirmModal = (confirmOption: { message: string; setOption: number }) => {
    setModalSetting({
      type: 'confirm',
      option: confirmOption.message,
      setOption: confirmOption.setOption,
    })
    !isOpen && toggle()
  }
  const handleDevideSktCancel = () => {
    resetCallback()
    toggle()
    window.location.reload()
  }
  const handleDevideSktConfirm = () => {
    toggle()
    navigate('/cancel')
  }
  const handleAlertReloadConfirm = () => {
    toggle()
    window.location.reload()
  }

  const validationCheck = () => {
    if (TelNum[0].state.length < 11) {
      setModalSetting({
        type: 'alert',
        option: '',
        setOption: 200,
      })
      toggle()
      return false
    }
    return true
  }

  const checkMvno = async () => {
    CheckMvmoSubmit().then((data) => {
      if (data?.Body.Type === 1) {
        setModalSetting({
          //KT Mvno 모달 띄우기
          type: 'MVNO',
          option: { title: data.Body.TermsTitle, content: data.Body.Terms },
          setOption: 0,
        })
        toggle()
      } else {
        securityCheck()
      }
    })
  }
  const setMvnoTermsAgree = async (flag: number) => {
    await new Promise((resolve) => {
      setTermsAgree((prev) => {
        const termsAgreeCopy = [...prev]
        termsAgreeCopy[8] = flag
        resolve(termsAgreeCopy)
        return termsAgreeCopy
      })
    })
  }

  const cancelSecureSubmit = (douModalOpen: any, modaltoggle: any) => {
    ResetDouModalOption()
    switch (true) {
      case SecurityNum?.length < 4:
        setDouModalSetting('보안문자를 확인해 주세요.')
        douModalOpen()
        break
      default:
        CancelSecuritySubmit().then(async (data) => {
          if (data?.Header?.ErrCode === 0) {
            await ResetTimeOut()
            setTranId(data?.Body?.TranID)
            setTimeOut(data?.Body?.TimeOut)
            setCertCheck(true)
            setDouModalSetting(
              '입력하신 번호로 인증번호가 전송되었습니다. 전송된 인증번호를 입력해 주세요.',
            )
            ResetSecurityNum()
            douModalOpen()
          } else if (data?.Header?.ErrCode === 1051) {
            alert(data?.Header?.ErrMsg)
            modaltoggle()
          } else {
            await setDouModalSetting(data?.Header?.ErrMsg)
            ResetSecurityNum()
            douModalOpen()
          }
        })
        break
    }
  }
  const joinSecureSubmit = (douModalOpen: any, modaltoggle: any) => {
    ResetDouModalOption()
    switch (true) {
      case SecurityNum?.length < 4:
        setDouModalSetting('보안문자를 확인해 주세요.')
        douModalOpen()
        break
      default:
        JoinSecuritySubmit().then(async (data) => {
          if (data?.Header?.ErrCode === 0) {
            await ResetTimeOut()
            setTranId(data?.Body?.TranID)
            setTimeOut(data?.Body?.TimeOut)
            setCertCheck(true)
            setDouModalSetting(
              '입력하신 번호로 인증번호가 전송되었습니다.\n 전송된 인증번호를 입력해 주세요.',
            )
            ResetSecurityNum()
            douModalOpen()
          } else if (data?.Header?.ErrCode === 1021) {
            alert(data?.Header?.ErrMsg)
            window.location.reload()
          } else if (data?.Header?.ErrCode === 1050) {
            alert(data?.Header?.ErrMsg)
            modaltoggle()
          } else {
            await setDouModalSetting(data?.Header?.ErrMsg)
            ResetSecurityNum()
            douModalOpen()
          }
        })
        break
    }
  }
  const refundResultContent = (refund: RefundResponseType): string => {
    const isSkt = TelType === 0
    const amount = Number(refund?.Amount).toLocaleString()
    const info = `${refund.BankName}/${refund.Accountnumber}/${refund.AccountName}`
    const displayDate = refund?.RegDT
      ? dayjs(refund.RegDT).format('YYYY년 MM월 DD일')
      : dayjs().format('YYYY년 MM월 DD일')

    if (isSkt) {
      return (
        `이용해 주셔서 감사합니다.<br /><br />` +
        `${displayDate} 환불 접수되었으며 ${amount}원이 영업일 기준 2일 이내 입금될 예정입니다.<br />` +
        `궁금하신 사항은 고객센터 1855-3631로 연락 부탁 드립니다.<br /><br />` +
        `※ 계좌번호, 예금주 등 입력하신 환불정보에 문제가 있을 경우 환불이 지연될 수 있습니다.<br /><br />` +
        `입력하신 정보: ${info}<br />` +
        `입금자명: 휴대폰가족보호 환불`
      )
    }

    return (
      `이용해 주셔서 감사합니다.<br /><br />` +
      `${amount}원이 영업일 7일 이내 입금될 예정입니다.<br />` +
      `※ 계좌번호, 예금주 등 입력하신 환불정보에 문제가 있을 경우 환불이 지연될 수 있습니다.<br /><br />` +
      `입력하신 정보: ${info}`
    )
  }

  const refundSecureSubmit = (
    douModalOpen: () => void,
    modaltoggle: () => void,
    openRefundModalFn: (option: { title: string; content: string }) => void,
    openAlertModalFn: (alertOption: { message: string; setOption: number }) => void,
    openConfirmModalFn: (confirmOption: { message: string; setOption: number }) => void,
  ) => {
    ResetDouModalOption()
    switch (true) {
      case SecurityNum?.length < 4:
        setDouModalSetting('보안문자를 확인해 주세요.')
        douModalOpen()
        break
      default:
        RefundSecuritySubmit().then(async (data) => {
          if (data?.Header?.ErrCode === 0) {
            await ResetTimeOut()
            setTranId(data?.Body?.TranID)
            setTimeOut(data?.Body?.TimeOut)
            setCertCheck(true)
            setDouModalSetting('인증번호가 성공적으로 전송됐습니다.')
            ResetSecurityNum()
            douModalOpen()
          } else if (data?.Header?.ErrCode === 1066) {
            resetCallback()
            openAlertModalFn({
              message: data?.Header?.ErrMsg,
              setOption: 1066,
            })
          } else if (data?.Header?.ErrCode === 1063) {
            await setDouModalSetting(data?.Header?.ErrMsg)
            douModalOpen()
            modaltoggle()
          } else if (data?.Header?.ErrCode === 1065) {
            openConfirmModalFn({
              message: data?.Header?.ErrMsg,
              setOption: 1065,
            })
          } else if (data?.Header?.ErrCode === 1062) {
            openRefundModalFn({
              title: refundCompleteTitle,
              content: refundResultContent(data?.Body),
            })
          } else {
            await setDouModalSetting(data?.Header?.ErrMsg ?? '휴대폰 번호를 확인하세요.')
            ResetSecurityNum()
            douModalOpen()
          }
        })
        break
    }
  }

  return {
    join,
    executeJoin,
    cancel,
    refund,
    login,
    isOpen,
    modalSetting,
    douIsOpen,
    toggle,
    cancelSecureSubmit,
    joinSecureSubmit,
    refundSecureSubmit,
    secureModalCall,
    loginCertSubmit,
    douToggle,
    cookies,
    CancelIsLoading,
    JoinIsLoading,
    LoginIsLoading,
    RefundIsLoading,
    securityCheck,
    openRefundModal,
    openAlertModal,
    openConfirmModal,
    handleDevideSktCancel,
    handleDevideSktConfirm,
    handleAlertReloadConfirm,
  }
}
export default useSubscriptQuery
